import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { useTheme } from '@cp/ds/src/hooks';
import { CheckmarkIcon, CloseIcon } from '@cp/ds/src/icons';
import { Typography } from '@cp/ds/src/components/typography';
import { useEffect, useMemo } from 'react';
import { IToastItem } from './types';
import { useToastManager } from './useToastManager';
import { TOAST_TYPES } from './const';

interface ToastItemProps {
  toast: IToastItem;
}

export const ToastItem = ({ toast }: ToastItemProps) => {
  const { type, signal, content } = toast;
  const manager = useToastManager();
  const theme = useTheme();

  const [mainColor, innerIcon] = useMemo(() => {
    const defaultIcon: string = '!';
    const defaultColor = theme.palette['gray dark'];
    switch (type) {
      case TOAST_TYPES.SUCCESS:
        return [theme.palette.green, <CheckmarkIcon />];
      case TOAST_TYPES.ERROR:
        return [theme.palette['brand pink hover'], defaultIcon];
      default:
        return [defaultColor, defaultIcon];
    }
  }, [theme.palette, type]);

  const closeHandle = () => manager.close(toast.id);

  useEffect(() => {
    if (!signal) return;
    signal.addEventListener('abort', closeHandle);
    return () => signal.removeEventListener('abort', closeHandle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={2}
      sx={{
        backgroundColor: mainColor,
        boxShadow: '0px -2px 16px 0px #00000021',
        borderRadius: 4,
        padding: 2,
        width: '100%',
        pointerEvents: 'all',
        minHeight: 64,
      }}
    >
      <Box
        sx={{
          color: mainColor,
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: theme.palette.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {innerIcon}
      </Box>
      <Box sx={{ flex: '1 1', color: theme.palette.white }}>
        {typeof content === 'string' ? (
          <Typography sx={{ display: 'block', lineHeight: '16px' }} variant="small text">
            {content}
          </Typography>
        ) : (
          content
        )}
      </Box>
      <Box
        component="button"
        onClick={closeHandle}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: theme.palette.white,
        }}
      >
        <CloseIcon />
      </Box>
    </Stack>
  );
};
